import { defineStore } from 'pinia';
import * as API from '@vp/common/api';
import { useConnectionStore } from '../connection';
import { ref } from 'vue';
import { LoadingState } from '@vp/common/util';
import * as Sentry from '@sentry/vue';

type PermissionKey = API.App.Permission.UserPermission.PermissionKey;
type PermissionLevel = API.App.Permission.UserPermission.PermissionLevel;
type PermissionRecord = Record<PermissionKey, PermissionLevel>;

export const usePermissionsStore = defineStore('@vp/permissions', () => {
  const connectionStore = useConnectionStore();
  const gqlApiClient = connectionStore.gqlApiClient!;

  const loadingState = ref<LoadingState>(LoadingState.UNKNOWN);
  const permissionsMap = ref<PermissionRecord>({} as PermissionRecord);

  let inFlight: Promise<PermissionRecord> | null = null;

  const fetchPermissions = async () => {
    try {
      loadingState.value = LoadingState.LOADING;

      const permissions = await API.App.Permission.Api.fetchUserPermissions(gqlApiClient);

      permissionsMap.value = Object.fromEntries(
        permissions.map(p => [p.key, p.level]),
      ) as PermissionRecord;

      loadingState.value = LoadingState.DONE;
    } catch (e) {
      loadingState.value = LoadingState.ERROR;
      Sentry.captureException(e);
    }

    return permissionsMap.value;
  };

  // Load once per store instance. Since each custom element gets its own Pinia,
  // several components (and installPlugins) call this during bootstrap; de-dupe
  // concurrent calls to a single fetch and skip re-fetching once loaded so we
  // never transiently blank out the map and flicker permission-gated UI.
  const loadPermissions = async () => {
    if (loadingState.value === LoadingState.DONE) {
      return permissionsMap.value;
    }

    if (!inFlight) {
      inFlight = fetchPermissions().finally(() => {
        inFlight = null;
      });
    }

    return inFlight;
  };

  return {
    permissionsMap,
    loadPermissions,
    loadingState,
  };
});
